import { useEffect, useState } from "react";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { STATES } from "@/lib/constants";

export function useIndustries() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/blueprints/industries").then((r) => setList(r.data)); }, []);
  return list;
}

export function IndustryPicker({ value, onChange, list }) {
  const [open, setOpen] = useState(false);
  const groups = [...new Set(list.map((i) => i.group))];
  const cur = list.find((i) => i.key === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="industry-picker-trigger">{cur ? cur.label : "Search industries…"}<ChevronsUpDown className="h-4 w-4 opacity-50" /></Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
        <Command>
          <CommandInput placeholder="Type to search…" data-testid="industry-picker-search" />
          <CommandList>
            <CommandEmpty>No industry found — pick "Other / General Commercial".</CommandEmpty>
            {groups.map((g) => (
              <CommandGroup key={g} heading={g}>
                {list.filter((i) => i.group === g).map((i) => (
                  <CommandItem key={i.key} value={i.label} onSelect={() => { onChange(i.key); setOpen(false); }} data-testid={`industry-option-${i.key}`}>
                    <Check className={`h-4 w-4 mr-2 text-emerald-600 ${value === i.key ? "opacity-100" : "opacity-0"}`} />{i.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export const Toggle = ({ options, value, onChange, testid }) => (
  <div className="inline-flex rounded-md border border-slate-300 overflow-hidden" data-testid={testid}>
    {options.map(([k, l]) => <button type="button" key={k} onClick={() => onChange(k)} data-testid={`${testid}-${k}`} className={`px-4 py-2 text-sm transition-colors ${value === k ? "bg-[#0b1730] text-white" : "text-slate-600 hover:bg-slate-50"}`}>{l}</button>)}
  </div>
);

const Field = ({ label, children }) => <label className="block text-xs font-semibold text-slate-600">{label}<div className="mt-1">{children}</div></label>;
const num = (v) => (v === "" || v == null ? null : Number(v));

export function BlueprintModal({ initial, onClose, onSubmit, busy }) {
  const list = useIndustries();
  const [f, setF] = useState({ industry: "", mode: "lease", state: "", city: "", county: "", sqft: "", capacity: "", price_psf: "", monthly_rent: "", purchase_price: "", business_name: "", ...initial });
  const [spaceBy, setSpaceBy] = useState(initial?.capacity && !initial?.sqft ? "capacity" : "sqft");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const ind = list.find((i) => i.key === f.industry);
  const submit = () => {
    const out = { ...f };
    ["sqft", "capacity", "price_psf", "monthly_rent", "purchase_price"].forEach((k) => { out[k] = num(out[k]); });
    if (spaceBy === "sqft") out.capacity = null; else out.sqft = null;
    if (out.mode === "lease") out.purchase_price = null; else out.monthly_rent = null;
    onSubmit(out);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="blueprint-modal">
        <DialogHeader>
          <DialogTitle className="font-display">Compliance Blueprint inputs</DialogTitle>
          <DialogDescription>Every field is optional except industry. We fill sensible regional defaults you can edit later.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Business industry type"><IndustryPicker value={f.industry} onChange={(v) => setF({ ...f, industry: v })} list={list} /></Field>
          <Field label="Property execution"><Toggle options={[["lease", "Lease"], ["purchase", "Purchase"]]} value={f.mode} onChange={(v) => setF({ ...f, mode: v })} testid="bp-mode" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="State">
              <select value={f.state} onChange={set("state")} className="w-full h-10 border border-slate-300 rounded-md px-2 text-sm bg-white" data-testid="bp-state-select"><option value="">Select…</option>{STATES.map((s) => <option key={s}>{s}</option>)}</select>
            </Field>
            <Field label="City"><Input value={f.city} onChange={set("city")} data-testid="bp-city-input" /></Field>
            <Field label="County"><Input value={f.county} onChange={set("county")} data-testid="bp-county-input" /></Field>
          </div>
          <Field label="Spatial constraints">
            <div className="space-y-2">
              <Toggle options={[["sqft", "Known square footage"], ["capacity", `Target ${ind ? ind.capacity_label.toLowerCase() : "capacity"}`]]} value={spaceBy} onChange={setSpaceBy} testid="bp-space-by" />
              {spaceBy === "sqft" ? <Input type="number" min="0" placeholder="e.g. 4,000 sq ft" value={f.sqft ?? ""} onChange={set("sqft")} data-testid="bp-sqft-input" />
                : <Input type="number" min="0" placeholder={ind ? ind.capacity_label : "Capacity"} value={f.capacity ?? ""} onChange={set("capacity")} data-testid="bp-capacity-input" />}
            </div>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={f.mode === "lease" ? "Price per sq ft (annual)" : "Price per sq ft"}><Input type="number" min="0" value={f.price_psf ?? ""} onChange={set("price_psf")} data-testid="bp-price-psf-input" /></Field>
            {f.mode === "lease"
              ? <Field label="Monthly base rent"><Input type="number" min="0" value={f.monthly_rent ?? ""} onChange={set("monthly_rent")} data-testid="bp-monthly-rent-input" /></Field>
              : <Field label="Total property purchase price"><Input type="number" min="0" value={f.purchase_price ?? ""} onChange={set("purchase_price")} data-testid="bp-purchase-price-input" /></Field>}
          </div>
          <Field label="Business name (for the appendix)"><Input value={f.business_name} onChange={set("business_name")} data-testid="bp-business-name-input" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={!f.industry || busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="bp-modal-submit">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Build blueprint</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
